import { Tooltip } from "~/components/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import {
    AccessSegment,
    canViewColumn,
    ColumnMetadata,
    RowSizes,
} from "~/components/DataGrid";
import { DataGridCellValue } from "~/components/DataGrid/DataGridCellValue";
import { useUser } from "~/modules/user/hooks/useUser";
import { cn } from "~/lib/utils";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { useRegistrationModal } from "~/modules/auth/hooks/useRegistrationModal";
import { isCatalogSlug } from "~/modules/catalog/lib/isCatalogSlug";
import { CatalogTooltip } from "~/modules/catalog/components/CatalogTooltip";
import { AssetUserEvents } from "~/modules/analytics/events/assets";

// // // //

function DescriptionTooltip(props: {
    description: string | React.ReactNode | undefined;
}) {
    const { description } = props;
    if (!description) {
        return null;
    }

    if (typeof description === "string" && isCatalogSlug(description)) {
        return (
            <CatalogTooltip
                slug={description}
                iconClassName="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground-hover transition-colors cursor-help"
                placement="top"
            />
        );
    }

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="px-3 py-2 text-sm max-w-xs">{description}</div>
            }
        >
            {({ setTriggerRef }) => (
                <span ref={setTriggerRef} className="flex">
                    <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground-hover transition-colors cursor-help"
                    />
                </span>
            )}
        </Tooltip>
    );
}

// // // //

function FactTableItem<RowType, ColType>(props: {
    column: ColumnMetadata<RowType, ColType>;
    row: RowType;
    striped: boolean;
    isLoggedIn?: boolean;
    loginRequiredOnParentCard?: boolean;
    analyticsTableName: string;
}) {
    const {
        column,
        row,
        isLoggedIn = false,
        loginRequiredOnParentCard = false,
        analyticsTableName,
    } = props;

    // Flag whether or not to show the column
    const isContentLoginGated =
        !isLoggedIn && !canViewColumn(AccessSegment.Free, column.access);
    const blurValue = isContentLoginGated || column.blur;

    const showLoginToUnlock = isContentLoginGated && !loginRequiredOnParentCard;
    const { open: openRegistrationModal } = useRegistrationModal();

    return (
        <div
            className={cn(
                "px-5 py-2 flex flex-wrap justify-between items-center",
                // {
                //     "bg-grid-row": !striped,
                //     "bg-grid-row-striped": striped,
                // },
            )}
        >
            <div className="flex flex-col">
                <ClassnamePassthruWrapper
                    className={cn({
                        "flex items-center gap-1.5": column.tooltip,
                    })}
                >
                    <div className="text-foreground text-sm">
                        {column.label}
                    </div>
                    <DescriptionTooltip description={column.tooltip} />
                </ClassnamePassthruWrapper>
            </div>

            <ClassnamePassthruWrapper
                className={cn({
                    "flex items-center gap-1.5": showLoginToUnlock,
                })}
            >
                {/* NOTE - we only show this when loginRequiredOnParentCard is false */}
                {showLoginToUnlock && (
                    <button
                        type="button"
                        onClick={() => {
                            openRegistrationModal({
                                label: AssetUserEvents.FactsTableClickLoginToUnlock,
                                properties: {
                                    column: column.slug,
                                    tableName: analyticsTableName,
                                },
                            });
                        }}
                        className="cursor-pointer text-link hover:text-link-hover transition-colors select-none text-xs flex items-center gap-1.5 font-semibold whitespace-nowrap w-fit mt-0.5"
                    >
                        <FontAwesomeIcon
                            icon={faLock}
                            className="h-2.5 w-2.5"
                        />
                        Log in to unlock
                    </button>
                )}

                {!showLoginToUnlock && (
                    <div
                        className={cn("text-sm text-foreground", {
                            "blur-xs pointer-events-none select-none":
                                blurValue,
                        })}
                    >
                        {column.render !== undefined &&
                            column.render(row, {
                                size: RowSizes.small,
                                width: column.width,
                                renderOptions: column.renderOptions,
                            })}
                        {column.render === undefined && (
                            <DataGridCellValue
                                size={RowSizes.small}
                                type={column.type}
                                value={column.cellValue(row)}
                            />
                        )}
                    </div>
                )}
            </ClassnamePassthruWrapper>
        </div>
    );
}

export function FactsTable<RowType, ColType>(props: {
    columns: ColumnMetadata<RowType, ColType>[];
    row: RowType;
    loginRequiredOnParentCard?: boolean;
    includeBottomBorder?: boolean;
    placeholderRows?: number;
    analyticsTableName: string;
}) {
    const {
        columns,
        row,
        loginRequiredOnParentCard = false,
        includeBottomBorder = false,
        analyticsTableName,
    } = props;
    const { isLoggedIn } = useUser();

    return (
        <div
            className={cn("grid grid-cols-1 divide-y", {
                "border-b": includeBottomBorder,
            })}
        >
            {columns.map((c, idx) => (
                <FactTableItem
                    key={c.slug}
                    row={row}
                    column={c}
                    striped={idx % 2 === 0}
                    isLoggedIn={isLoggedIn}
                    loginRequiredOnParentCard={loginRequiredOnParentCard}
                    analyticsTableName={analyticsTableName}
                />
            ))}
        </div>
    );
}
